import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';
import FeedbackThread from '../../components/feedback/FeedbackThread';
import { MessageSquare, Send, Loader2, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'bug_report', label: 'Bug Report' },
  { value: 'feature_request', label: 'Feature Request' },
  { value: 'general', label: 'General' },
  { value: 'complaint', label: 'Complaint' },
  { value: 'praise', label: 'Praise' },
];

const SENTIMENTS = [
  { value: 'positive', label: 'Positive', color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30' },
  { value: 'neutral', label: 'Neutral', color: 'text-slate-400 bg-slate-700 border-slate-600' },
  { value: 'negative', label: 'Negative', color: 'text-red-400 bg-red-500/10 border-red-500/30' },
];

const AccountantFeedback = () => {
  const { user } = useAuth();
  const [myFeedback, setMyFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('general');
  const [sentiment, setSentiment] = useState('neutral');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchMyFeedback();
    // Mark all my accountant feedback as read when I open this page
    axios.put(`${API_URL}/api/accountant/feedback/mark-read`).catch(() => {});
  }, []);

  const fetchMyFeedback = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/accountant/feedback`);
      setMyFeedback(res.data.feedback || []);
    } catch (err) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/accountant/feedback`, {
        category, sentiment, message: message.trim(), page: 'accountant_feedback'
      });
      toast.success('Feedback submitted!');
      setMessage('');
      setCategory('general');
      setSentiment('neutral');
      fetchMyFeedback();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const statusBadge = { open: 'bg-amber-500/15 text-amber-400', reviewed: 'bg-sky-500/15 text-sky-400', resolved: 'bg-emerald-500/15 text-emerald-400' };

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="accountant-feedback-page">
      <div>
        <h1 className="text-2xl font-bold text-white">Feedback</h1>
        <p className="text-slate-400 text-sm mt-1">Help us improve the Accountant Portal</p>
      </div>

      {/* Submit Form */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-base flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-sky-400" />
            Submit Feedback
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex gap-2 flex-wrap">
              {CATEGORIES.map(c => (
                <button key={c.value} type="button" onClick={() => setCategory(c.value)}
                  className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${
                    category === c.value ? 'bg-sky-600 text-white border-sky-600' : 'bg-slate-700 text-slate-400 border-slate-600 hover:border-slate-500'
                  }`}>{c.label}</button>
              ))}
            </div>
            <div className="flex gap-2">
              {SENTIMENTS.map(s => (
                <button key={s.value} type="button" onClick={() => setSentiment(s.value)}
                  className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${
                    sentiment === s.value ? s.color : 'bg-slate-700 text-slate-400 border-slate-600 hover:border-slate-500'
                  }`}>{s.label}</button>
              ))}
            </div>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)}
              placeholder="Tell us what you think..."
              rows={4}
              className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-sky-500 resize-none"
              data-testid="feedback-message" />
            <Button type="submit" disabled={submitting || !message.trim()}
              className="bg-sky-600 hover:bg-sky-700 h-9 text-sm" data-testid="submit-feedback-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Submit Feedback
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Previous Feedback */}
      {myFeedback.length > 0 && (
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-sm">Your Previous Feedback</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {myFeedback.map(fb => (
              <div key={fb.id} className="p-3 bg-slate-700/30 rounded-lg border border-slate-600 space-y-2">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${statusBadge[fb.status] || ''}`}>{fb.status}</span>
                  <span className="text-[10px] text-slate-500">{fb.category?.replace('_', ' ')}</span>
                  <span className="text-[10px] text-slate-600">
                    {new Date(fb.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                  </span>
                  {fb.unread_for_me && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full border text-amber-400 bg-amber-500/15 border-amber-500/40 font-medium">
                      New reply
                    </span>
                  )}
                </div>
                <FeedbackThread
                  feedbackId={fb.id}
                  thread={fb.thread || []}
                  viewerRole="user"
                  endpoint={`${API_URL}/api/accountant/feedback/${fb.id}/message`}
                  onMessageSent={fetchMyFeedback}
                  compact
                />
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AccountantFeedback;
